import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, X, Copy, Check, QrCode, Bitcoin as BitcoinIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { QRCodeSVG } from "qrcode.react";

const SHARE_PLATFORMS = [
  { id: "twitter", name: "X", icon: () => <span className="text-white font-bold text-sm">X</span>, getUrl: (text, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
  { id: "facebook", name: "FB", icon: () => <span className="text-[#1877F2] font-bold text-sm">f</span>, getUrl: (text, url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}` },
  { id: "whatsapp", name: "WA", icon: () => <span className="text-[#25D366] font-bold text-sm">W</span>, getUrl: (text, url) => `https://wa.me/?text=${encodeURIComponent(text + " " + url)}` },
  { id: "telegram", name: "TG", icon: () => <span className="text-[#0088CC] font-bold text-sm">T</span>, getUrl: (text, url) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
];

function ShareCardContent({ type, data }) {
  if (type === "quiz_result") {
    return (
      <div className="w-[400px] bg-gradient-to-br from-[#0A0A0A] to-[#161616] border border-[#F7931A]/40 rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#F7931A]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
            <BitcoinIcon className="text-[#F7931A]" size={16} />
          </div>
          <span className="text-[#F7931A] font-heading font-bold text-sm tracking-wider">BITCOIN BRAIN</span>
        </div>
        <h3 className="font-heading text-2xl font-bold text-white mb-1">{data.title || "Quiz Complete"}</h3>
        <p className="text-zinc-500 text-sm mb-4">{data.subtitle || ""}</p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-[#F7931A] font-mono font-bold text-2xl">{data.correct || 0}</p>
            <p className="text-zinc-600 text-xs">Correct</p>
          </div>
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-white font-mono font-bold text-2xl">{data.total || 0}</p>
            <p className="text-zinc-600 text-xs">Questions</p>
          </div>
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-green-400 font-mono font-bold text-2xl">+{data.xp || 0}</p>
            <p className="text-zinc-600 text-xs">XP</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-zinc-600 text-xs">bitcoinbrain.space</p>
          <p className="text-zinc-700 text-xs">Learn Bitcoin. Own It.</p>
        </div>
      </div>
    );
  }

  if (type === "achievement") {
    return (
      <div className="w-[400px] bg-gradient-to-br from-[#0A0A0A] to-[#161616] border border-[#F7931A]/40 rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#F7931A] to-[#FF5F1F]" />
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
            <BitcoinIcon className="text-[#F7931A]" size={16} />
          </div>
          <span className="text-[#F7931A] font-heading font-bold text-sm tracking-wider">BITCOIN BRAIN</span>
        </div>
        <div className="text-center mb-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#F7931A]/20 border-2 border-[#F7931A]/50 flex items-center justify-center mb-3">
            <span className="text-3xl">{data.icon || <BitcoinIcon className="text-[#F7931A]" size={32} />}</span>
          </div>
          <h3 className="font-heading text-xl font-bold text-white">{data.title || "Achievement Unlocked"}</h3>
          <p className="text-zinc-400 text-sm mt-1">{data.description || ""}</p>
        </div>
        {data.stats && (
          <div className="bg-black/40 rounded-xl p-3 flex items-center justify-between mb-3">
            <span className="text-zinc-500 text-sm">{data.stats.label}</span>
            <span className="text-[#F7931A] font-mono font-bold">{data.stats.value}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <p className="text-zinc-600 text-xs">bitcoinbrain.space</p>
          <p className="text-zinc-700 text-xs">Learn Bitcoin. Own It.</p>
        </div>
      </div>
    );
  }

  if (type === "journey_stats") {
    return (
      <div className="w-[400px] bg-gradient-to-br from-[#0A0A0A] to-[#161616] border border-[#F7931A]/40 rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#F7931A]/3 rounded-full translate-y-1/2 translate-x-1/2" />
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
            <BitcoinIcon className="text-[#F7931A]" size={16} />
          </div>
          <span className="text-[#F7931A] font-heading font-bold text-sm tracking-wider">BITCOIN BRAIN</span>
        </div>
        <h3 className="font-heading text-xl font-bold text-white mb-1">My Bitcoin Journey</h3>
        <p className="text-zinc-500 text-sm mb-4">{data.user_name || ""}</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-[#F7931A] font-mono font-bold text-lg">{data.total_btc || "0.00000000"}</p>
            <p className="text-zinc-600 text-xs">BTC Stacked</p>
          </div>
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-white font-mono font-bold text-lg">{data.purchase_count || 0}</p>
            <p className="text-zinc-600 text-xs">Purchases</p>
          </div>
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-green-400 font-mono font-bold text-lg">{data.pnl_percent || "0"}%</p>
            <p className="text-zinc-600 text-xs">P&L</p>
          </div>
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-zinc-300 font-mono font-bold text-lg">${data.avg_cost || "0"}</p>
            <p className="text-zinc-600 text-xs">Avg Cost</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-zinc-600 text-xs">bitcoinbrain.space</p>
          <p className="text-zinc-700 text-xs">Stack sats. Stay humble.</p>
        </div>
      </div>
    );
  }

  if (type === "course_progress") {
    return (
      <div className="w-[400px] bg-gradient-to-br from-[#0A0A0A] to-[#161616] border border-[#F7931A]/40 rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#F7931A] via-[#FF5F1F] to-[#F7931A]" />
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
            <BitcoinIcon className="text-[#F7931A]" size={16} />
          </div>
          <span className="text-[#F7931A] font-heading font-bold text-sm tracking-wider">BITCOIN BRAIN</span>
        </div>
        <h3 className="font-heading text-xl font-bold text-white mb-1">{data.title || "Course Progress"}</h3>
        <p className="text-zinc-500 text-sm mb-4">{data.user_name || ""}</p>
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-zinc-400">Sections Complete</span>
            <span className="text-[#F7931A] font-mono font-bold">{data.completed || 0}/21</span>
          </div>
          <div className="w-full bg-black/60 rounded-full h-3 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#F7931A] to-[#FF5F1F] rounded-full transition-all" style={{ width: `${((data.completed || 0) / 21) * 100}%` }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-[#F7931A] font-mono font-bold text-lg">{data.total_xp || 0}</p>
            <p className="text-zinc-600 text-xs">Total BTC XP</p>
          </div>
          <div className="bg-black/40 rounded-xl p-3 text-center">
            <p className="text-white font-heading font-bold text-sm">{data.xp_title || "Fiat Sleeper"}</p>
            <p className="text-zinc-600 text-xs">Rank</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-zinc-600 text-xs">bitcoinbrain.space</p>
          <p className="text-zinc-700 text-xs">Learn Bitcoin. Own It.</p>
        </div>
      </div>
    );
  }

  return null;
}

export function ShareCardModal({ isOpen, onClose, type, data, shareText, shareUrl }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const copyShareText = useCallback(() => {
    navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  }, [shareText, shareUrl]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 w-full max-w-lg"
            onClick={e => e.stopPropagation()}
            data-testid="share-card-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-bold text-white">Share Your Progress</h3>
              <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="share-card-close">
                <X size={20} />
              </button>
            </div>

            {/* Toggle: Card Preview / QR Code */}
            <div className="flex justify-center gap-2 mb-4">
              <button
                onClick={() => setShowQR(false)}
                className={`px-4 py-1.5 rounded-lg text-xs font-medium border transition-all ${!showQR ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]" : "bg-[#161616] border-white/10 text-zinc-400"}`}
                data-testid="share-tab-card"
              >
                Card
              </button>
              <button
                onClick={() => setShowQR(true)}
                className={`px-4 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1 ${showQR ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]" : "bg-[#161616] border-white/10 text-zinc-400"}`}
                data-testid="share-tab-qr"
              >
                <QrCode size={12} /> QR Code
              </button>
            </div>

            {/* Card Preview */}
            {!showQR && (
              <div className="flex justify-center mb-5 overflow-x-auto" ref={cardRef}>
                <div className="transform scale-[0.85] origin-top">
                  <ShareCardContent type={type} data={data} />
                </div>
              </div>
            )}

            {/* QR Code */}
            {showQR && (
              <div className="flex flex-col items-center mb-5" data-testid="share-qr-section">
                <div className="bg-white rounded-2xl p-5 mb-3">
                  <QRCodeSVG
                    value={shareUrl}
                    size={200}
                    bgColor="#FFFFFF"
                    fgColor="#0A0A0A"
                    level="M"
                  />
                </div>
                <p className="text-zinc-500 text-xs text-center">Scan to visit Bitcoin Brain</p>
                <p className="text-[#F7931A] text-xs font-mono mt-1">{shareUrl}</p>
              </div>
            )}

            {/* Share buttons */}
            <div className="flex gap-2 mb-4">
              {SHARE_PLATFORMS.map(p => (
                <a
                  key={p.id}
                  href={p.getUrl(shareText, shareUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 p-3 rounded-xl border border-white/10 bg-[#161616] hover:border-[#F7931A]/50 transition-all"
                  data-testid={`share-card-${p.id}`}
                >
                  <p.icon />
                  <span className="text-white text-xs">{p.name}</span>
                </a>
              ))}
            </div>

            {/* Copy */}
            <button
              onClick={copyShareText}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border border-white/10 bg-[#161616] hover:border-[#F7931A]/50 transition-all"
              data-testid="share-card-copy"
            >
              {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} className="text-zinc-400" />}
              <span className="text-zinc-300 text-sm">{copied ? "Copied!" : "Copy Link & Text"}</span>
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ShareCardButton({ type, data, shareText, shareUrl, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 text-zinc-400 hover:text-[#F7931A] transition-colors ${className}`}
        data-testid="share-card-trigger"
      >
        <Share2 size={16} />
        <span className="text-sm">Share</span>
      </button>
      <ShareCardModal
        isOpen={open}
        onClose={() => setOpen(false)}
        type={type}
        data={data}
        shareText={shareText}
        shareUrl={shareUrl}
      />
    </>
  );
}
